import { z } from 'zod/mini'
import { getMessage } from '../../../../common/utils/MessageUtils'
import _every from 'lodash/every'
import { convertSchemaErrors } from '../../../common/Form'

export const UserClassificationAddCategorySchema = z.object({
  code: z.string(),
  value: z.string().check(z.minLength(1, getMessage('E010001', '値'))),
  rank: z.optional(z.number()),
  validStartDate: z.nullable(z.date()),
  validEndDate: z.nullable(z.date()),
  memo: z.optional(z.string())
})

/**
 * 有効期間が重複しているかチェック
 *
 * @function isDateRangeOverlapping
 * @param {Date | null} startA - 期間Aの開始日
 * @param {Date | null} endA - 期間Aの終了日
 * @param {Date | null} startB - 期間Bの開始日
 * @param {Date | null} endB - 期間Bの終了日
 * @returns {boolean} 重複している場合true
 */
export const isDateRangeOverlapping = (startA, endA, startB, endB) => {
  // nullの場合は無期限として扱う
  // 開始日がnull: 過去から有効
  // 終了日がnull: 未来まで有効

  // 両方の期間が無期限の場合（開始日も終了日もnull）
  if (!startA && !endA && !startB && !endB) {
    return true
  }

  // 期間Aが無期限の場合（開始日も終了日もnull）
  if (!startA && !endA) {
    return true
  }

  // 期間Bが無期限の場合（開始日も終了日もnull）
  if (!startB && !endB) {
    return true
  }

  // 開始日のみnullの場合、過去から有効として扱う
  const actualStartA = startA || new Date(0) // 1970-01-01
  const actualStartB = startB || new Date(0) // 1970-01-01

  // 終了日のみnullの場合、未来まで有効として扱う
  const actualEndA = endA || new Date(8640000000000000) // 最大日付
  const actualEndB = endB || new Date(8640000000000000) // 最大日付

  // 期間が重複する条件: (startA <= endB) && (startB <= endA)
  return actualStartA <= actualEndB && actualStartB <= actualEndA
}

/** ランク整合性エラーメッセージ */
export const RANK_CONSISTENCY_ERROR_MESSAGE = 'ランクは全て空であるか、全ての項目に値を設定してください'

/**
 * 単一区分のランク整合性をチェックする。 削除されていない区分項目について、ランクが「全て設定」または「全て空」でない場合はエラー。
 *
 * @function checkRankConsistency
 * @param {Object[]} records - チェック対象の区分項目レコード配列
 * @returns {boolean} 整合していればtrue、不整合ならfalse
 */
export const checkRankConsistency = (records) => {
  const activeRecords = (records || []).filter((record) => !record.isDeleted)

  if (activeRecords.length === 0) {
    return true
  }

  const hasRank = (record) => record.rank != null && record.rank !== '' && !isNaN(record.rank)

  const recordsWithRank = activeRecords.filter(hasRank)
  const recordsWithoutRank = activeRecords.filter((record) => !hasRank(record))

  // 一部のみ設定されている場合は不整合
  return !(recordsWithRank.length > 0 && recordsWithoutRank.length > 0)
}

/**
 * 登録対象の全区分についてランク整合性をチェックし、不整合な区分コードを列挙する。 画面上で選択中の区分だけでなく、未反映の全区分を検証するために使用する （選択中でない区分のランク不整合がバックエンドへ反映される不具合の再発防止用）。
 *
 * @function findRankInconsistentClassifications
 * @param {string[]} classificationCodes - 検証対象の区分コード配列
 * @param {Object<string, Object[]>} classificationRecordsMap - 区分コード → レコード配列
 * @returns {string[]} ランク不整合の区分コード配列
 */
export const findRankInconsistentClassifications = (classificationCodes, classificationRecordsMap) => {
  return (classificationCodes || []).filter((code) => !checkRankConsistency(classificationRecordsMap?.[code] || []))
}

/**
 * 値とコードの一意性をチェック（有効期間の重複も考慮）
 *
 * @function checkIfValueIsUnique
 * @param {Array} existingRecords - 既存のレコード配列
 * @param {string[]} fields - チェックするフィールド名の配列
 * @param {Object} values - チェックする値のオブジェクト
 * @param {boolean} editMode - 編集モードかどうか
 * @returns {Object} 各フィールドの一意性チェック結果（true: 一意、false: 重複）
 */
export const checkIfValueIsUnique = (existingRecords, fields, values, editMode) => {
  const init = Object.fromEntries(fields.map((e) => [e, true]))

  const editKey = editMode ? values.key : null

  // 比較用の正規化関数（空白除去・トリム）
  const normalize = (value) => {
    if (value == null) return ''
    return String(value).trim()
  }

  const normalizedValueValue = normalize(values.value)
  const normalizedValueCode = normalize(values.code)

  // 重複チェックを実行
  return existingRecords.reduce((acc, c) => {
    if (editMode && c.key === editKey) return acc
    if (c.isDeleted) return acc // 削除済みのレコードは除外

    const normalizedExistingValue = normalize(c.value)
    const normalizedExistingCode = normalize(c.code)

    // 値の重複チェック: 値が同じで、かつ有効期間が重複している場合のみ重複と判定
    if (normalizedExistingValue === normalizedValueValue) {
      const hasOverlap = isDateRangeOverlapping(
        values.validStartDate,
        values.validEndDate,
        c.validStartDate,
        c.validEndDate
      )
      if (hasOverlap) {
        acc.value = false
      }
    }

    // コードの重複チェック: コードが同じで、かつ有効期間が重複している場合のみ重複と判定
    if (normalizedValueCode !== '' && normalizedExistingCode === normalizedValueCode) {
      const hasOverlap = isDateRangeOverlapping(
        values.validStartDate,
        values.validEndDate,
        c.validStartDate,
        c.validEndDate
      )
      if (hasOverlap) {
        acc.code = false
      }
    }

    return acc
  }, init)
}

/**
 * フォームのバリデーション
 *
 * @function validateForm
 * @param {Object} formDataRef - フォームデータ
 * @param {Array} existingRecords - 既存のレコード配列
 * @param {boolean} editMode - 編集モードかどうか
 * @returns {Object | null} エラーオブジェクト（エラーがない場合はnull）
 */
export const validateForm = (formDataRef, existingRecords = [], editMode = false) => {
  const idLabelMap = Object.freeze({
    code: 'コード',
    value: '値',
    validStartDate: '有効開始月',
    validEndDate: '有効終了月'
  })

  // バリデーション前に、rankが空文字列またはnullの場合はundefinedに変換
  const normalizedFormData = {
    ...formDataRef,
    rank: formDataRef.rank === '' || formDataRef.rank === null ? undefined : formDataRef.rank
  }

  const errors = convertSchemaErrors(UserClassificationAddCategorySchema, normalizedFormData)

  // ランクの数値チェック（値が入力されている場合のみ）
  if (normalizedFormData.rank !== undefined && normalizedFormData.rank !== null && normalizedFormData.rank !== '') {
    if (typeof normalizedFormData.rank !== 'number' || isNaN(normalizedFormData.rank)) {
      if (!errors.rank) {
        errors.rank = []
      }
      errors.rank.push('ランクは数値で入力してください')
    }
  }

  // 有効期間の上下関係チェック（内部は月初日・月末日で保持）
  if (normalizedFormData.validStartDate && normalizedFormData.validEndDate) {
    if (normalizedFormData.validStartDate > normalizedFormData.validEndDate) {
      if (!errors.validEndDate) {
        errors.validEndDate = []
      }
      errors.validEndDate.push('有効終了月は有効開始月と同じかその後を指定してください')
    }
  }

  // 有効期間の形式チェック（月選択のため Date オブジェクトであることを確認）
  if (normalizedFormData.validStartDate && !(normalizedFormData.validStartDate instanceof Date)) {
    if (!errors.validStartDate) {
      errors.validStartDate = []
    }
    errors.validStartDate.push('有効開始月はYYYY/MM形式で選択してください')
  }
  if (normalizedFormData.validEndDate && !(normalizedFormData.validEndDate instanceof Date)) {
    if (!errors.validEndDate) {
      errors.validEndDate = []
    }
    errors.validEndDate.push('有効終了月はYYYY/MM形式で選択してください')
  }

  // コードと値の重複チェック（有効期間の重複も考慮）
  const fields = ['code', 'value']
  const isUnique = checkIfValueIsUnique(existingRecords, fields, normalizedFormData, editMode)

  for (const field of fields) {
    if (!errors[field]) {
      errors[field] = []
    }
    if (!isUnique[field]) {
      const message =
        field === 'code'
          ? '同一のコードで有効期間が重複しているものがあります'
          : '同一の値で有効期間が重複しているものがあります'
      errors[field].push(message)
    }
  }

  if (_every(errors, (e) => e.length === 0)) return null
  return errors
}
